/**
 * TimingBatcher — Singleton that accumulates operation timings in memory
 * and flushes them to DB via a single batch RPC every 30 seconds.
 *
 * Performance: recordTiming() is Array.push() — O(1), ~0.001ms, zero I/O.
 * Flush: 1 RPC per 30s interval (not per operation).
 * Errors: logged silently, never shown to user.
 */

import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/shared/utils/logger';

interface TimingEntry {
  operation_type: string;
  duration_ms: number;
  touch_point: string;
  created_at: string;
}

const FLUSH_INTERVAL_MS = 30_000;
const queue: TimingEntry[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;
let visibilityHandler: (() => void) | null = null;

/**
 * @description Push a timing entry into the in-memory queue. O(1), zero I/O.
 * Entries are flushed to DB via batch RPC every 30s by the batcher.
 */
export function recordTiming(
  operationType: string,
  durationMs: number,
  touchPoint?: string,
): void {
  queue.push({
    operation_type: operationType,
    duration_ms: durationMs,
    touch_point: touchPoint ?? 'admin',
    created_at: new Date().toISOString(),
  });
}

/** Flush queue to DB via single batch RPC. */
async function flush(): Promise<void> {
  if (queue.length === 0) return;
  const batch = queue.splice(0);
  try {
    const { error } = await supabase.rpc('record_operation_timings_batch', {
      p_timings: batch,
    });
    if (error) logger.warn('[Timer] Batch flush failed:', error.message);
  } catch {
    logger.warn('[Timer] Batch flush network error, dropped', batch.length, 'entries');
  }
}

/** Start the periodic flush. Call once at app init. */
export function startTimingBatcher(): void {
  if (flushTimer) return;
  flushTimer = setInterval(flush, FLUSH_INTERVAL_MS);

  visibilityHandler = () => {
    if (document.visibilityState === 'hidden' && queue.length > 0) {
      flush();
    }
  };
  document.addEventListener('visibilitychange', visibilityHandler);
}

/** Stop the batcher. For cleanup/logout. */
export function stopTimingBatcher(): void {
  if (flushTimer) {
    clearInterval(flushTimer);
    flushTimer = null;
  }
  if (visibilityHandler) {
    document.removeEventListener('visibilitychange', visibilityHandler);
    visibilityHandler = null;
  }
  flush();
}
